import { useDaily, useDailyEvent, useMeetingState } from '@daily-co/daily-react'
import { useAutoUpdatingRef, usePrevious } from '@quickmd-llc/frontend-utils'
import { useEffect, useRef } from 'react'
import { useDailyVisitInfo } from './useDailyVisitInfo'
import { type PatientWithVisitInfo } from 'types/types'
import { useCallIsActive } from './useCallIsActive'
import { captureException } from '@sentry/react'
import { useSelector } from 'store/hooks/useSelector'
import { playDialOutTone } from '../playDialOutTone'

type Input = {
  patientWithVisitInfo: PatientWithVisitInfo
  preferredAudioOn: boolean
  preferredVideoOn: boolean
  isPrepMode: boolean
}

export function useHandleConnectToDaily({
  patientWithVisitInfo,
  preferredAudioOn,
  preferredVideoOn,
  isPrepMode,
}: Input) {
  const activeVisitId = useSelector((state) => state.waitingRoomReducer.visitInfo?.VisitId)
  const dailyDialOutData = useSelector((state) => state.waitingRoomReducer.dailyDialOutData)
  const dailyDialOutDataRef = useAutoUpdatingRef(dailyDialOutData)
  const preferredAudioOnRef = useAutoUpdatingRef(preferredAudioOn)
  const preferredVideoOnRef = useAutoUpdatingRef(preferredVideoOn)

  const lastSuccessfulVisitIdConnection = useRef<string | null>(null)

  const { callIsActive } = useCallIsActive()

  const roomUrl = useSelector((state) => state.waitingRoomReducer.roomUrl)

  const previousRoomUrl = usePrevious(roomUrl)

  if (callIsActive) {
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    lastSuccessfulVisitIdConnection.current = activeVisitId
  }
  if (previousRoomUrl && previousRoomUrl !== roomUrl) {
    lastSuccessfulVisitIdConnection.current = null
  }

  const lastJoinAttemptDidConnectWithPatient = lastSuccessfulVisitIdConnection.current === activeVisitId

  const dailyVisitInfo = useDailyVisitInfo()

  const meetingState = useMeetingState()

  const callObject = useDaily()
  const joinUnnecessary = meetingState === 'joined-meeting' || meetingState === 'joining-meeting'
  const joinUnnecessaryRef = useAutoUpdatingRef(joinUnnecessary)

  const callObjectRef = useAutoUpdatingRef(callObject)

  useDailyEvent('dialout-connected', () => {
    playDialOutTone()
  })

  useEffect(() => {
    if (
      isPrepMode ||
      joinUnnecessaryRef.current ||
      !callObjectRef.current ||
      !dailyVisitInfo ||
      !patientWithVisitInfo?.PatientNumber
    ) {
      return
    }
    lastSuccessfulVisitIdConnection.current = null
    const { url, token } = dailyVisitInfo

    callObjectRef.current
      .join({
        url,
        token,
        startAudioOff: !preferredAudioOnRef.current,
        startVideoOff: !preferredVideoOnRef.current,
      })
      .then(() => {
        if (dailyVisitInfo.isPhoneCall) {
          // @ts-expect-error TS18047 -- MIGRATION: not approved, fix when encountered
          callObjectRef.current.startDialOut({
            phoneNumber: patientWithVisitInfo.PatientNumber,
            // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
            callerId: dailyDialOutDataRef.current.callerId,
            displayName: 'patient',
            userId: 'patient',
          })
        }
      })
      .catch((e) => {
        console.error('❌ Daily join failed', e)
        captureException(e, {
          extra: {
            url,
            token,
            dailyDialOutData: dailyDialOutDataRef.current,
            phoneNumber: patientWithVisitInfo.PatientNumber,
          },
        })
      })
  }, [
    isPrepMode,
    dailyVisitInfo,
    callObjectRef,
    joinUnnecessaryRef,
    patientWithVisitInfo?.PatientNumber,
    dailyDialOutDataRef,
    preferredAudioOnRef,
    preferredVideoOnRef,
  ])

  return { lastJoinAttemptDidConnectWithPatient }
}
